/**
 * Beregningsmotor til Vognmand Onboarding.
 *
 * To veje, der mødes:
 *  1. Baglæns fra ønsket personlig indkomst: hvad skal virksomheden omsætte?
 *  2. Fremad fra forventede kørselsindtægter: hvad bliver der tilbage til ejeren?
 *
 * Alle forudsætninger er synlige og kan ændres af brugeren. Ingen skjulte branchetal.
 * Alle beløb er i danske kroner og EKSKLUSIVE moms, fordi momsen ikke er vognmandens penge.
 */

// Satser er forudsætninger, ikke sandheder. De vises for brugeren og kan rettes.
export const SATSER = {
  arbejdsuger: 52,
  amBidrag: 0.08,          // arbejdsmarkedsbidrag
  trækprocent: 0.38,       // typisk samlet trækprocent, brugeren kan rette den
  månedsfradrag: 4800,     // personfradrag og beskæftigelsesfradrag pr. måned, afrundet
  selskabsskat: 0.22,
  feriepengeSats: 0.125,   // af løn til ansatte chauffører
  atpOgPension: 0.04,
  scenarier: { konservativt: 0.85, forventet: 1, optimistisk: 1.15 },
};

const tal = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const positiv = (v) => Math.max(0, tal(v));

/** Trin 2: arbejdstid. Ikke alle timer giver omsætning, derfor belægningsgraden. */
export function beregnArbejdstid(s, satser = SATSER) {
  const dageOmUgen = (s.arbejdsdage || []).length;
  const timerOmUgen = positiv(s.timerOmUgen);
  const ferieuger = Math.min(positiv(s.ferieuger), 20);
  const arbejdsuger = Math.max(1, satser.arbejdsuger - ferieuger);
  const belægning = Math.min(Math.max(tal(s.belægning) || 70, 5), 100) / 100;

  const timerOmMåneden = (timerOmUgen * arbejdsuger) / 12;
  const dageOmMåneden = (dageOmUgen * arbejdsuger) / 12;
  return {
    dageOmUgen,
    timerOmUgen,
    arbejdsuger,
    belægning,
    timerOmMåneden: Math.round(timerOmMåneden),
    dageOmMåneden: Math.round(dageOmMåneden * 10) / 10,
    fakturerbareTimerOmMåneden: Math.round(timerOmMåneden * belægning),
    timerOmÅret: Math.round(timerOmUgen * arbejdsuger),
  };
}

/** Trin 4: alle løbende omkostninger pr. måned. Hver post tælles én gang. */
export function beregnOmkostninger(s) {
  const antalBiler = Math.max(1, tal(s.antalBiler) || 1);
  const kmOmMåneden = positiv(s.kmOmMåneden);
  const prisPrKm = positiv(s.energiPrKm);

  const poster = {
    leasingEllerFinansiering: positiv(s.leasing) * antalBiler,
    forsikring: positiv(s.forsikring) * antalBiler,
    ejerafgift: positiv(s.ejerafgift) * antalBiler,
    energi: prisPrKm > 0 ? Math.round(kmOmMåneden * prisPrKm) * antalBiler : positiv(s.energi) * antalBiler,
    serviceOgDæk: positiv(s.service) * antalBiler,
    bogføringOgAdministration: positiv(s.administration),
    abonnementerOgPlatforme: positiv(s.abonnementer),
    øvrigeDriftsomkostninger: positiv(s.øvrige),
  };

  // Ansatte chauffører: løn plus feriepenge, ATP og pension.
  const chaufførLøn = positiv(s.chaufførLønOmMåneden) * Math.max(0, tal(s.antalChauffører));
  poster.chaufførløn = Math.round(chaufførLøn * (1 + SATSER.feriepengeSats + SATSER.atpOgPension));

  const total = Object.values(poster).reduce((a, b) => a + b, 0);
  return { poster, total: Math.round(total), antalBiler };
}

/** Provision til kørselskontor eller platform tages af omsætningen, ikke af overskuddet. */
function efterProvision(omsætning, provisionPct) {
  const p = Math.min(Math.max(tal(provisionPct), 0), 60) / 100;
  return { netto: omsætning * (1 - p), provision: Math.round(omsætning * p) };
}

/** Trin 5: forventet omsætning ud fra den valgte indtægtsmodel. Modellerne blandes aldrig. */
export function beregnOmsætning(s, arbejdstid) {
  const model = s.indtægtsmodel || "time";
  let brutto = 0;
  let grundlag = "";

  if (model === "time") {
    const timer = positiv(s.fakturerbareTimer) || arbejdstid.fakturerbareTimerOmMåneden;
    brutto = timer * positiv(s.timepris);
    grundlag = `${timer} fakturerbare timer × ${positiv(s.timepris)} kr.`;
  } else if (model === "dag") {
    const dage = positiv(s.fakturerbareDage) || Math.round(arbejdstid.dageOmMåneden);
    brutto = dage * positiv(s.dagspris);
    grundlag = `${dage} kørselsdage × ${positiv(s.dagspris)} kr.`;
  } else if (model === "km") {
    const km = positiv(s.fakturerbareKm) || positiv(s.kmOmMåneden);
    brutto = km * positiv(s.kmPris);
    grundlag = `${km} km × ${positiv(s.kmPris)} kr.`;
  } else {
    brutto = positiv(s.kontraktindtægt);
    grundlag = "fast kontraktindtægt pr. måned";
  }

  const { netto, provision } = efterProvision(brutto, s.provision);
  return { brutto: Math.round(brutto), provision, netto: Math.round(netto), grundlag, model };
}

/** Fra virksomhedens resultat til kronerne på ejerens konto. */
export function beregnEjerindkomst(resultatFørEjer, s, satser = SATSER) {
  const aps = (s.virksomhedsform || "ikke_afklaret") === "aps";
  const opsparing = positiv(s.opsparingOmMåneden);
  const tilRådighed = Math.max(0, resultatFørEjer - opsparing);

  if (aps) {
    // I et ApS er ejerløn en omkostning, og resten beskattes som selskabets overskud.
    const løn = Math.min(tilRådighed, positiv(s.ønsketLøn) || tilRådighed);
    const selskabsoverskud = Math.max(0, tilRådighed - løn);
    return {
      form: "ApS",
      ejerløn: Math.round(løn),
      selskabsoverskud: Math.round(selskabsoverskud),
      selskabsskat: Math.round(selskabsoverskud * satser.selskabsskat),
      overførtTilEgenkapital: Math.round(selskabsoverskud * (1 - satser.selskabsskat)),
      førSkat: Math.round(løn),
      efterSkat: Math.round(efterPersonskat(løn, s, satser)),
      virksomhedsopsparing: opsparing,
    };
  }
  return {
    form: "Personligt ejet",
    ejerløn: Math.round(tilRådighed),
    selskabsoverskud: 0,
    selskabsskat: 0,
    overførtTilEgenkapital: 0,
    førSkat: Math.round(tilRådighed),
    efterSkat: Math.round(efterPersonskat(tilRådighed, s, satser)),
    virksomhedsopsparing: opsparing,
  };
}

/** Et ESTIMAT. Den præcise skat afhænger af kommune, fradrag og personlige forhold. */
export function efterPersonskat(førSkat, s, satser = SATSER) {
  const træk = Math.min(Math.max(tal(s.trækprocent) || satser.trækprocent * 100, 20), 56) / 100;
  const fradrag = tal(s.månedsfradrag) || satser.månedsfradrag;
  const efterAm = førSkat * (1 - satser.amBidrag);
  return Math.max(0, efterAm - Math.max(0, efterAm - fradrag) * træk);
}

/** Baglæns: hvad skal der omsættes, for at ejeren får sit mål udbetalt? */
export function beregnOmsætningsbehov(s, omkostninger, arbejdstid, satser = SATSER) {
  const mål = positiv(s.indkomstmål);
  const efterSkatØnsket = (s.målErEfterSkat || false);
  // Regn målet om til et beløb før personskat, hvis brugeren tænker i udbetalt løn.
  let førSkat = mål;
  if (efterSkatØnsket && mål > 0) {
    førSkat = mål;
    for (let i = 0; i < 40; i += 1) {
      const diff = mål - efterPersonskat(førSkat, s, satser);
      if (Math.abs(diff) < 10) break;
      førSkat += diff * 1.4;
    }
  }
  const andenIndkomst = positiv(s.andenIndkomst);
  const behovFraVirksomhed = Math.max(0, førSkat - andenIndkomst);
  const opsparing = positiv(s.opsparingOmMåneden);

  const aps = (s.virksomhedsform || "") === "aps";
  const resultatBehov = behovFraVirksomhed + opsparing;
  const nettoOmsætningsbehov = omkostninger.total + resultatBehov;
  const provision = Math.min(Math.max(tal(s.provision), 0), 60) / 100;
  const bruttoOmsætningsbehov = nettoOmsætningsbehov / (1 - provision);

  const dage = Math.max(1, arbejdstid.dageOmMåneden);
  const timer = Math.max(1, arbejdstid.fakturerbareTimerOmMåneden);
  return {
    målFørSkat: Math.round(førSkat),
    målEfterSkat: Math.round(efterSkatØnsket ? mål : efterPersonskat(førSkat, s, satser)),
    resultatBehov: Math.round(resultatBehov),
    nettoOmsætningsbehov: Math.round(nettoOmsætningsbehov),
    omsætningsbehov: Math.round(bruttoOmsætningsbehov),
    breakEvenDrift: Math.round(omkostninger.total / (1 - provision)),
    prDag: Math.round(bruttoOmsætningsbehov / dage),
    prTime: Math.round(bruttoOmsætningsbehov / timer),
    timerNødvendige: Math.round(bruttoOmsætningsbehov / Math.max(1, positiv(s.timepris) || 1)),
    selskabsform: aps ? "ApS" : "Personligt ejet",
  };
}

/** Samler hele analysen. Ét kald, så dashboardet altid er konsistent. */
export function beregnAnalyse(svar, satser = SATSER) {
  const arbejdstid = beregnArbejdstid(svar, satser);
  const omkostninger = beregnOmkostninger(svar);
  const omsætning = beregnOmsætning(svar, arbejdstid);
  const behov = beregnOmsætningsbehov(svar, omkostninger, arbejdstid, satser);

  const resultatFørEjer = omsætning.netto - omkostninger.total;
  const ejer = beregnEjerindkomst(resultatFørEjer, svar, satser);

  const scenarier = Object.entries(satser.scenarier).map(([navn, faktor]) => {
    const brutto = Math.round(omsætning.brutto * faktor);
    const netto = Math.round(omsætning.netto * faktor);
    const resultat = netto - omkostninger.total;
    const e = beregnEjerindkomst(resultat, svar, satser);
    return { navn, omsætning: brutto, resultat: Math.round(resultat), ejerFørSkat: e.førSkat, ejerEfterSkat: e.efterSkat };
  });

  const målFørSkat = behov.målFørSkat;
  const forskel = ejer.førSkat - målFørSkat;
  const manglendeData = !arbejdstid.timerOmUgen || !omkostninger.total || !omsætning.brutto;

  let vurdering = "mangler_data";
  if (!manglendeData) {
    if (forskel >= 0) vurdering = "opnåeligt";
    else if (Math.abs(forskel) <= målFørSkat * 0.15) vurdering = "tæt_på";
    else vurdering = "kræver_ændringer";
  }

  return {
    arbejdstid,
    omkostninger,
    omsætning,
    behov,
    ejer,
    scenarier,
    resultatFørEjer: Math.round(resultatFørEjer),
    årligOmsætning: Math.round(omsætning.brutto * 12),
    likviditetsbehov: Math.round(omkostninger.total * (positiv(svar.betalingsdage) / 30 || 1)),
    sikkerhedsbeløb: positiv(svar.sikkerhedsbeløb),
    opstartskapital: positiv(svar.opstartskapital),
    engangsinvestering: positiv(svar.engangsinvestering),
    finansieringsbehov: Math.max(0, positiv(svar.engangsinvestering) - positiv(svar.opstartskapital)),
    målFørSkat,
    målEfterSkat: behov.målEfterSkat,
    forskel: Math.round(forskel),
    vurdering,
  };
}

export const kr = (v) =>
  `${Math.round(tal(v)).toLocaleString("da-DK")} kr.`;
